import { useCallback, useEffect, useMemo, useState } from 'react';
import { Button, Col, Popover, Row, Table, Tooltip, Typography } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { ClusterOutlined, InfoCircleOutlined, ReloadOutlined } from '@ant-design/icons';
import { getOversightRollup } from '@/api/oversight';
import { isNotAvailable, normalizeApiError } from '@/api/errors';
import type { OversightRollup, OversightSpokeRow } from '@/api/types';
import { EmptyState } from '@/components/EmptyState';
import { ErrorState } from '@/components/ErrorState';
import { PageHeader } from '@/components/PageHeader';
import { TableSkeleton } from '@/components/TableSkeleton';
import { usePageTitle } from '@/components/pageTitle';
import { formatRelative } from '@/lib/time';
import { MetricCell } from '@/oversight/badges';
import { AlertsPanel } from '@/oversight/AlertsPanel';
import { SpokeDrawer } from '@/oversight/SpokeDrawer';

const { Text } = Typography;

type NumericTotalKey =
  | 'entityCount'
  | 'openCaseCount'
  | 'slaBreachCount'
  | 'unresolvedSignalCount'
  | 'reconciliationBreakCount';

interface Kpi {
  key: NumericTotalKey;
  label: string;
  kind: 'benign' | 'breach' | 'signal';
  hint: string;
}

const KPIS: Kpi[] = [
  { key: 'entityCount', label: 'Entities', kind: 'benign', hint: 'Entities under management across all spokes.' },
  { key: 'openCaseCount', label: 'Open cases', kind: 'benign', hint: 'Open case runs across all spokes.' },
  { key: 'slaBreachCount', label: 'SLA breaches', kind: 'breach', hint: 'Cases past their SLA. Investigate first.' },
  { key: 'unresolvedSignalCount', label: 'Unresolved signals', kind: 'signal', hint: 'Signals awaiting triage.' },
  { key: 'reconciliationBreakCount', label: 'Recon breaks', kind: 'breach', hint: 'Reconciliation breaks awaiting resolution.' },
];

function TotalsStrip({ totals }: { totals: OversightRollup['totals'] }) {
  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
        gap: 12,
        marginBottom: 16,
      }}
    >
      {KPIS.map((k) => {
        const v = totals[k.key];
        return (
          <Tooltip key={k.key} title={k.hint}>
            <div
              className="ol-card"
              style={{ padding: '12px 14px' }}
              role="group"
              aria-label={`${k.label}: ${v == null ? 'no source wired' : v}`}
            >
              <Text type="secondary" style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                {k.label}
              </Text>
              <div className="ol-num" style={{ marginTop: 4, fontSize: 22, fontWeight: 700 }}>
                {/* null = no source wired — MetricCell renders "—", never a fabricated 0. */}
                <MetricCell value={v} kind={k.kind} />
              </div>
            </div>
          </Tooltip>
        );
      })}
    </div>
  );
}

/** The per-field provenance map (which source fed each aggregate, or why it is null). */
function SourcesPopover({ sources }: { sources?: Record<string, string> }) {
  if (!sources || Object.keys(sources).length === 0) return null;
  return (
    <Popover
      placement="bottomRight"
      title="Data sources"
      content={
        <div style={{ maxWidth: 420 }}>
          {Object.entries(sources).map(([field, source]) => (
            <div key={field} style={{ marginBottom: 8 }}>
              <div className="ol-mono" style={{ fontSize: 11, fontWeight: 700 }}>
                {field}
              </div>
              <div style={{ fontSize: 11.5, color: 'var(--ol-text-secondary)', lineHeight: 1.5 }}>
                {source}
              </div>
            </div>
          ))}
        </div>
      }
    >
      <Button size="small" type="text" icon={<InfoCircleOutlined />} aria-label="Data sources">
        Sources
      </Button>
    </Popover>
  );
}

export function OversightPage() {
  usePageTitle('Hub oversight');

  const [rollup, setRollup] = useState<OversightRollup | null>(null);
  const [loading, setLoading] = useState(true);
  const [unavailable, setUnavailable] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [openSpoke, setOpenSpoke] = useState<{ spokeId: string; displayName?: string } | null>(null);
  const now = Date.now();

  const load = useCallback(async () => {
    setLoading(true);
    setUnavailable(false);
    setError(null);
    try {
      setRollup(await getOversightRollup());
    } catch (e) {
      const n = normalizeApiError(e);
      if (isNotAvailable(n)) setUnavailable(true);
      else setError(n.message);
      setRollup(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const spokeOptions = useMemo(
    () =>
      (rollup?.spokes ?? []).map((s) => ({
        value: s.spokeId,
        label: s.displayName ? `${s.displayName} (${s.spokeId})` : s.spokeId,
      })),
    [rollup],
  );

  const columns: ColumnsType<OversightSpokeRow> = [
    {
      title: 'Spoke',
      dataIndex: 'spokeId',
      key: 'spokeId',
      fixed: 'left',
      render: (_, r) => (
        <div style={{ minWidth: 150 }}>
          <div style={{ fontWeight: 600 }}>{r.displayName ?? r.spokeId}</div>
          {r.displayName && (
            <Text className="ol-mono" style={{ fontSize: 10.5, color: 'var(--ol-text-tertiary)' }}>
              {r.spokeId}
            </Text>
          )}
        </div>
      ),
    },
    {
      title: 'Entities',
      dataIndex: 'entityCount',
      key: 'entityCount',
      width: 108,
      align: 'right',
      sorter: (a, b) => (a.entityCount ?? -1) - (b.entityCount ?? -1),
      render: (v: number | null) => <MetricCell value={v} kind="benign" />,
    },
    {
      title: 'Open cases',
      dataIndex: 'openCaseCount',
      key: 'openCaseCount',
      width: 116,
      align: 'right',
      sorter: (a, b) => (a.openCaseCount ?? -1) - (b.openCaseCount ?? -1),
      render: (v: number | null) => <MetricCell value={v} kind="benign" />,
    },
    {
      title: 'SLA breaches',
      dataIndex: 'slaBreachCount',
      key: 'slaBreachCount',
      width: 130,
      align: 'right',
      defaultSortOrder: 'descend',
      sorter: (a, b) => (a.slaBreachCount ?? -1) - (b.slaBreachCount ?? -1),
      render: (v: number | null) => <MetricCell value={v} kind="breach" />,
    },
    {
      title: 'Unresolved signals',
      dataIndex: 'unresolvedSignalCount',
      key: 'unresolvedSignalCount',
      width: 156,
      align: 'right',
      sorter: (a, b) => (a.unresolvedSignalCount ?? -1) - (b.unresolvedSignalCount ?? -1),
      render: (v: number | null) => <MetricCell value={v} kind="signal" />,
    },
    {
      title: 'Recon breaks',
      dataIndex: 'reconciliationBreakCount',
      key: 'reconciliationBreakCount',
      width: 128,
      align: 'right',
      sorter: (a, b) => (a.reconciliationBreakCount ?? -1) - (b.reconciliationBreakCount ?? -1),
      render: (v: number | null) => <MetricCell value={v} kind="breach" />,
    },
    {
      title: 'Last review',
      dataIndex: 'lastReviewAt',
      key: 'lastReviewAt',
      width: 120,
      render: (v?: string) =>
        v ? (
          <Text style={{ fontSize: 12 }} title={v}>
            {formatRelative(v, now)}
          </Text>
        ) : (
          <Text type="secondary" style={{ fontSize: 12 }}>
            never
          </Text>
        ),
    },
  ];

  if (unavailable) {
    return (
      <div className="ol-page">
        <EmptyState
          icon={<ClusterOutlined />}
          title="Hub oversight console"
          description="An RE-grade roll-up across every spoke this hub oversees — the surface an MLRO scans each morning. It lands once the oversight API is live (Track A, PS-4)."
          bullets={[
            'Per-spoke roll-up: entities, open cases, SLA breaches, unresolved signals, recon breaks',
            'A totals header so the whole book reads at a glance',
            'Row → drill-down: case breakdown, breaches, reconciliation, recent reviews',
            'A severity-ranked alerts feed, filterable by type and spoke',
          ]}
        />
      </div>
    );
  }

  return (
    <div className="ol-page">
      <PageHeader
        title="Hub oversight"
        icon={<ClusterOutlined />}
        subtitle="Every spoke this hub oversees, at a glance. Scan the totals, sort by what is on fire, open a spoke to drill in. SLA breaches and reconciliation breaks are pulled to the top."
        extra={
          <>
            {rollup?.generatedAt && (
              <Text type="secondary" style={{ fontSize: 11 }} title={rollup.generatedAt}>
                as of {formatRelative(rollup.generatedAt, now)}
              </Text>
            )}
            <SourcesPopover sources={rollup?.sources} />
            <Button icon={<ReloadOutlined />} onClick={() => void load()} loading={loading}>
              Refresh
            </Button>
          </>
        }
      />

      {error && (
        <ErrorState what="the oversight roll-up" detail={error} onRetry={() => void load()} compact />
      )}

      {rollup && <TotalsStrip totals={rollup.totals} />}

      <Row gutter={[16, 16]}>
        <Col xs={24} xl={16}>
          <Table<OversightSpokeRow>
            rowKey="spokeId"
            loading={loading && (rollup?.spokes.length ?? 0) > 0}
            columns={columns}
            dataSource={rollup?.spokes ?? []}
            size="small"
            locale={{
              emptyText: loading ? (
                <TableSkeleton label="Loading the spoke roll-up" rows={5} />
              ) : (
                <EmptyState
                  compact
                  icon={<ClusterOutlined />}
                  title="No spokes under this hub yet"
                  description="Spokes are the operating units inside this hub — a branch, a desk, a subsidiary. Provision one in Tenant admin and its case load, SLA breaches and reconciliation state roll up here."
                />
              ),
            }}
            scroll={{ x: 900 }}
            pagination={false}
            onRow={(r) => ({
              onClick: () => setOpenSpoke({ spokeId: r.spokeId, displayName: r.displayName ?? undefined }),
              style: { cursor: 'pointer' },
              tabIndex: 0,
              'aria-label': `Spoke ${r.displayName ?? r.spokeId} — open drill-down`,
              onKeyDown: (e: React.KeyboardEvent) => {
                if (e.key === 'Enter') {
                  setOpenSpoke({ spokeId: r.spokeId, displayName: r.displayName ?? undefined });
                }
              },
            })}
          />
        </Col>
        <Col xs={24} xl={8}>
          <div style={{ height: 560 }}>
            <AlertsPanel
              spokeOptions={spokeOptions}
              onFocusSpoke={(spokeId) => {
                const s = (rollup?.spokes ?? []).find((x) => x.spokeId === spokeId);
                setOpenSpoke(
                  s ? { spokeId: s.spokeId, displayName: s.displayName ?? undefined } : { spokeId },
                );
              }}
            />
          </div>
        </Col>
      </Row>

      <SpokeDrawer
        spokeId={openSpoke?.spokeId ?? null}
        displayName={openSpoke?.displayName}
        onClose={() => setOpenSpoke(null)}
      />
    </div>
  );
}
